import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { API_BASE_URL } from '../http/api-base-url.token';
import { joinUrl } from '../http/url.util';
import { AUTH_LOGIN_ROUTE } from './auth-login-route.token';
import { AuthIdentity, AuthSessionEngine, AuthTokens } from './auth-engine';

export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  userId: string;
  email: string;
  role: string;
}

export interface RegisterResult {
  userId: string;
  email: string;
  companyId: string;
}

/**
 * The COMPANY auth scheme: JWT + opaque refresh token against `auth/*` (B1). Storage prefix
 * `rekruto_`. The refresh grant is rotated on every refresh; logout revokes it server-side.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  // Field-initializer order matters: the engine is built (and restores localStorage) before the
  // signals below are derived from it.
  readonly #engine = new AuthSessionEngine(
    {
      storagePrefix: 'rekruto',
      refreshPath: 'auth/refresh',
      logoutPath: 'auth/logout',
      identityKeys: ['userId', 'email', 'role'],
      identityFromRefresh: (result): AuthIdentity => {
        const role = result['role'];
        return typeof role === 'string' ? { role } : {};
      },
      unauthenticatedPaths: [
        'auth/login',
        'auth/register',
        'auth/refresh',
        'auth/logout',
        'auth/verify-email',
        'auth/password-reset-request',
        'auth/password-reset-confirm',
      ],
    },
    this.http,
    this.apiBaseUrl,
    inject(Router),
    inject(AUTH_LOGIN_ROUTE),
  );

  readonly token = this.#engine.accessToken;
  readonly isAuthenticated = this.#engine.isAuthenticated;
  readonly email = this.#engine.identityValue('email');
  readonly role = this.#engine.identityValue('role');
  readonly userId = this.#engine.identityValue('userId');
  /** Endpoint paths whose own 401 must reach the caller, never trigger refresh or sign-out. */
  readonly unauthenticatedPaths = this.#engine.unauthenticatedPaths();

  login(email: string, password: string): Observable<LoginResult> {
    return this.http
      .post<LoginResult>(joinUrl(this.apiBaseUrl, 'auth/login'), { email, password })
      .pipe(
        tap(result =>
          this.#engine.adoptSession(
            { accessToken: result.accessToken, refreshToken: result.refreshToken },
            { userId: result.userId, email: result.email, role: result.role } satisfies AuthIdentity,
          ),
        ),
      );
  }

  /** Registers the first owner and their company; the account still needs email verification. */
  register(email: string, displayName: string, companyName: string, password: string): Observable<RegisterResult> {
    return this.http.post<RegisterResult>(joinUrl(this.apiBaseUrl, 'auth/register'), {
      email,
      displayName,
      companyName,
      password,
    });
  }

  verifyEmail(token: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(joinUrl(this.apiBaseUrl, 'auth/verify-email'), { token });
  }

  requestPasswordReset(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(joinUrl(this.apiBaseUrl, 'auth/password-reset-request'), { email });
  }

  confirmPasswordReset(token: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(joinUrl(this.apiBaseUrl, 'auth/password-reset-confirm'), {
      token,
      newPassword,
    });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(joinUrl(this.apiBaseUrl, 'auth/change-password'), {
      currentPassword,
      newPassword,
    });
  }

  /**
   * Exchanges the stored refresh grant for a fresh pair, rotating storage in place. One in-flight
   * refresh per scheme: concurrent callers share the same request.
   */
  refresh(): Observable<AuthTokens> {
    return this.#engine.refresh();
  }

  logout(): void {
    this.#engine.logout();
  }
}